import { ContactType } from '@arnold/common';
import { OrganizationTeamsQuery } from '../../generated/hooks';
import {
  constructEmployees,
  constructTeams,
  EmployeeRecord,
  EmployeeRecordWithId,
  TeamRecord,
} from './OrganizationImport';

export enum EmployeeImportState {
  New = 'new',
  Edited = 'edited',
  Unchanged = 'unchanged',
  NotSure = 'notSure',
}

export enum TeamImportState {
  New = 'new',
  Edited = 'edited',
  Unchanged = 'unchanged',
}

export interface EmployeeRecordWithAdditionalData
  extends Omit<EmployeeRecord, 'preferredContact' | 'surveyParticipant'> {
  preferredContact: ContactType;
  surveyParticipant: boolean;
  originalEmployeeRecord?: EmployeeRecordWithId;
  possibleMatches?: EmployeeRecordWithId[];
  id?: string;
  surveyIds: string[];
  surveyGroups: {
    surveyGroupId: string;
    addedOn: Date;
    runOngoingStepsIds?: string[];
  }[];
  state: EmployeeImportState;
}

interface TeamRecordWithAdditionalData extends Omit<TeamRecord, 'id' | 'supervisorTeam'> {
  id?: string;
  supervisorTeam?: string;
  state: TeamImportState;
}

export const createAdditionalDataForEmployees = (
  employees: EmployeeRecord[],
  organizationTeams: NonNullable<OrganizationTeamsQuery['organizationTeams']>,
): EmployeeRecordWithAdditionalData[] => {
  const originalEmployees = constructEmployees(organizationTeams, true);

  // Create separate Maps for faster lookup to find similar employees
  const emailIndex = new Map<string, EmployeeRecordWithId[]>();
  const otherEmailIndex = new Map<string, EmployeeRecordWithId[]>();
  const phoneIndex = new Map<string, EmployeeRecordWithId[]>();
  const firstnameIndex = new Map<string, EmployeeRecordWithId[]>();
  const surnameIndex = new Map<string, EmployeeRecordWithId[]>();
  const teamIndex = new Map<string, EmployeeRecordWithId[]>();

  // Build Maps
  originalEmployees.forEach((employee) => {
    if (employee.email) {
      const emails = emailIndex.get(employee.email) || [];
      emails.push(employee);
      emailIndex.set(employee.email, emails);
    }

    if (employee.otherEmail) {
      const otherEmails = otherEmailIndex.get(employee.otherEmail) || [];
      otherEmails.push(employee);
      otherEmailIndex.set(employee.otherEmail, otherEmails);
    }

    if (employee.phoneNumber) {
      const phones = phoneIndex.get(employee.phoneNumber) || [];
      phones.push(employee);
      phoneIndex.set(employee.phoneNumber, phones);
    }

    if (employee.firstname) {
      const firstnames = firstnameIndex.get(employee.firstname.toLowerCase()) || [];
      firstnames.push(employee);
      firstnameIndex.set(employee.firstname.toLowerCase(), firstnames);
    }

    if (employee.surname) {
      const surnames = surnameIndex.get(employee.surname.toLowerCase()) || [];
      surnames.push(employee);
      surnameIndex.set(employee.surname.toLowerCase(), surnames);
    }

    if (employee.team) {
      const teams = teamIndex.get(employee.team) || [];
      teams.push(employee);
      teamIndex.set(employee.team, teams);
    }
  });

  // Track employees that are already assigned as originalEmployeeRecord
  const assignedEmployeeIds = new Set<string>();

  // First pass - create all employees with their potential matches
  const initialResults = employees.map((employee): EmployeeRecordWithAdditionalData => {
    // Find potential matches using Maps
    const potentialMatches = new Set<EmployeeRecordWithId>();

    // Check Maps
    if (employee.email && emailIndex.has(employee.email)) {
      emailIndex.get(employee.email)?.forEach((e) => potentialMatches.add(e));
    }

    if (employee.otherEmail && otherEmailIndex.has(employee.otherEmail)) {
      otherEmailIndex.get(employee.otherEmail)?.forEach((e) => potentialMatches.add(e));
    }

    if (employee.phoneNumber && phoneIndex.has(employee.phoneNumber)) {
      phoneIndex.get(employee.phoneNumber)?.forEach((e) => potentialMatches.add(e));
    }

    if (employee.firstname) {
      const firstname = employee.firstname.toLowerCase();
      if (firstnameIndex.has(firstname)) {
        firstnameIndex.get(firstname)?.forEach((e) => potentialMatches.add(e));
      }
    }

    if (employee.surname) {
      const surname = employee.surname.toLowerCase();
      if (surnameIndex.has(surname)) {
        surnameIndex.get(surname)?.forEach((e) => potentialMatches.add(e));
      }
    }

    if (employee.team && teamIndex.has(employee.team)) {
      teamIndex.get(employee.team)?.forEach((e) => potentialMatches.add(e));
    }

    if (potentialMatches.size) {
      // Compare with potential matches
      const mostSimilarEmployees = findMostSimilarEmployees(employee, Array.from(potentialMatches));

      if (mostSimilarEmployees?.length) {
        const singleConfidentMatch = mostSimilarEmployees.length === 1 && mostSimilarEmployees[0].difference <= 1;
        // Duplicate org members that were removed from the sheet still match every remaining copy.
        // When every copy is identical — including fields that are empty on both sides — keep one
        // and let the other copies be removed, instead of asking to confirm each pair.
        const identicalCopies =
          mostSimilarEmployees.length > 1 && mostSimilarEmployees.every((match) => match.unchanged);
        const matchedEmployee =
          singleConfidentMatch || identicalCopies
            ? mostSimilarEmployees.find(
                (match) => !!match.employee.id && !assignedEmployeeIds.has(match.employee.id),
              ) ?? mostSimilarEmployees[0]
            : undefined;

        if (matchedEmployee?.employee.id) {
          assignedEmployeeIds.add(matchedEmployee.employee.id);
        }

        return {
          ...employee,
          state: matchedEmployee
            ? matchedEmployee.unchanged
              ? EmployeeImportState.Unchanged
              : EmployeeImportState.Edited
            : EmployeeImportState.NotSure,
          id: matchedEmployee ? matchedEmployee.employee.id : undefined,
          originalEmployeeRecord: matchedEmployee ? matchedEmployee.employee : undefined,
          possibleMatches: matchedEmployee ? undefined : mostSimilarEmployees.map((e) => e.employee),
          preferredContact: employee.preferredContact === 'SMS' ? ContactType.Sms : ContactType.Email,
          surveyParticipant: employee.surveyParticipant === 'Y' ? true : false,
          surveyIds: [],
          surveyGroups: [],
        };
      }
    }

    // If no similar employees found among potential matches
    return {
      ...employee,
      state: EmployeeImportState.New,
      preferredContact: employee.preferredContact === 'SMS' ? ContactType.Sms : ContactType.Email,
      surveyParticipant: employee.surveyParticipant === 'Y' ? true : false,
      surveyIds: [],
      surveyGroups: [],
    };
  });

  // This part is about deduplication - When there is some employee with name Martin Černý and we add another Martin Černý, we should have 2 of them.
  // but it created a problem where both of them are mapped to the same original employee, so we need to deduplicate them.
  const idToIndexes = new Map<string, number[]>();
  initialResults.forEach((result, index) => {
    if (result.id) {
      const arr = idToIndexes.get(result.id) || [];
      arr.push(index);
      idToIndexes.set(result.id, arr);
    }
  });

  idToIndexes.forEach((indexes) => {
    if (indexes.length <= 1) return;
    // We prefer to map the unchanged employee to the original one
    let preferredIndex = indexes.find((i) => {
      const res = initialResults[i];
      return res.originalEmployeeRecord !== undefined && allFieldsAreTheSame(employees[i], res.originalEmployeeRecord);
    });
    if (preferredIndex === undefined) {
      // If there is no unchanged employee, we map the first one to the original one - this case should never happen anyways
      preferredIndex = indexes[0];
    }

    indexes.forEach((i) => {
      if (i === preferredIndex) return;
      const res = initialResults[i];
      // Make a new employee out of the duplicate one
      initialResults[i] = {
        ...res,
        state: EmployeeImportState.New,
        id: undefined,
        originalEmployeeRecord: undefined,
        possibleMatches: undefined,
      };
    });
  });

  // Second pass - filter out assigned employees from possibleMatches
  return initialResults.map((result) => {
    // If there are no possible matches, just return the result as is
    if (!result.possibleMatches || result.possibleMatches.length === 0) {
      return result;
    }

    // Filter out any employees that are already assigned
    const filteredMatches = result.possibleMatches.filter((match) => !match.id || !assignedEmployeeIds.has(match.id));

    // Update the state if necessary
    let updatedState = result.state;
    if (result.state === EmployeeImportState.NotSure && filteredMatches.length === 0) {
      updatedState = EmployeeImportState.New;
    }

    return {
      ...result,
      possibleMatches: filteredMatches.length > 0 ? filteredMatches : undefined,
      state: updatedState,
    };
  });
};

const similarEmployeesSimilarityThreshold = 2; // at least 2 similarities to be considered similar
const similarEmployeesKeysToCompare = ['firstname', 'surname', 'email', 'otherEmail', 'phoneNumber', 'team'];
const findMostSimilarEmployees = (
  newEmployee: EmployeeRecord,
  oldEmployees: EmployeeRecordWithId[],
): { employee: EmployeeRecordWithId; similarity: number; difference: number; unchanged: boolean }[] | undefined => {
  // find similarities between employees, if there is 1 with 4 similarities and other has lower, return the one with 4
  // if there will be more than 1 with the same number of similarities, return all of them
  const employeesWithDifference = oldEmployees.map((oldEmployee) => {
    const differenceCount = differencesInEmployees(newEmployee, oldEmployee);
    return { employee: oldEmployee, difference: differenceCount };
  });
  const minDifferenceCount = Math.min(...employeesWithDifference.map((s) => s.difference));
  const leastDifferentEmployees = employeesWithDifference.filter((s) => s.difference === minDifferenceCount);
  const employeesWithAlsoSimilarities = leastDifferentEmployees.map((s) => {
    const similarity = similaritiesInEmployees(newEmployee, s.employee);
    return { employee: s.employee, difference: s.difference, similarity };
  });
  const mostSimilarEmployees = employeesWithAlsoSimilarities.filter(
    (s) => s.similarity >= similarEmployeesSimilarityThreshold,
  );
  if (mostSimilarEmployees.length === 0) {
    return undefined;
  }
  return mostSimilarEmployees.map((s) => ({
    ...s,
    unchanged: allFieldsAreTheSame(newEmployee, s.employee),
  }));
};

// Difference is when field that was already filled is different
// Ignore fields that were empty and are now filled
const differencesInEmployees = (
  newEmployee: EmployeeRecord,
  oldEmployee: EmployeeRecord,
  keysToCompare: string[] = similarEmployeesKeysToCompare,
): number => {
  let differences = 0;
  const keys = Object.keys(newEmployee) as (keyof EmployeeRecord)[];
  keys.forEach((key) => {
    if (keysToCompare.length > 0 && !keysToCompare.includes(key)) {
      return;
    }
    if (isEmptyEmployeeField(oldEmployee[key])) {
      return;
    }
    if (!areEmployeeFieldValuesEqual(newEmployee[key], oldEmployee[key])) {
      differences++;
    }
  });
  return differences;
};

// Similarities are when fields are the same
const similaritiesInEmployees = (
  newEmployee: EmployeeRecord,
  oldEmployee: EmployeeRecord,
  keysToCompare: string[] = similarEmployeesKeysToCompare,
): number => {
  let similarities = 0;
  const keys = Object.keys(newEmployee) as (keyof EmployeeRecord)[];
  keys.forEach((key) => {
    if (keysToCompare.length > 0 && !keysToCompare.includes(key)) {
      return;
    }
    if (!isEmptyEmployeeField(newEmployee[key]) && areEmployeeFieldValuesEqual(newEmployee[key], oldEmployee[key])) {
      similarities++;
    }
  });
  return similarities;
};

// Checks completely all fields - not only the ones that we based the similarity on
// even stuff like surveyParticipant and preferredContact
// we ignore id
const allFieldsAreTheSame = (newEmployee: EmployeeRecord, oldEmployee: EmployeeRecord): boolean => {
  const keys = Object.keys(newEmployee) as (keyof EmployeeRecord)[];
  return keys.every((key) => {
    if ((key as any) === 'id') {
      return true; // explicitly ignore id
    }
    return areEmployeeFieldValuesEqual(newEmployee[key], oldEmployee[key]);
  });
};

const isEmptyEmployeeField = (value: unknown): boolean => {
  if (value == null || value === '') {
    return true;
  }
  return Array.isArray(value) && value.length === 0;
};

// Empty string, null, and an empty list are the same value. Separate arrays with the same
// entries are the same value too — `[] !== []`, so a blank manager-of-team column used to
// show up as the only difference between two copies of one person.
export const areEmployeeFieldValuesEqual = (left: unknown, right: unknown): boolean => {
  if (isEmptyEmployeeField(left) && isEmptyEmployeeField(right)) {
    return true;
  }
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length && left.every((value, index) => areEmployeeFieldValuesEqual(value, right[index]))
    );
  }
  return left === right;
};

export const createAdditionalDataforTeams = (
  teams: TeamRecord[],
  organizationTeams: NonNullable<OrganizationTeamsQuery['organizationTeams']>,
): TeamRecordWithAdditionalData[] => {
  const originalTeams = constructTeams(organizationTeams);

  const teamsWithAdditionalData: TeamRecordWithAdditionalData[] = teams.map((team): TeamRecordWithAdditionalData => {
    // find team by name
    const originalTeam = originalTeams.find((t) => t.id === team.id);
    if (originalTeam) {
      const somethingChanged = originalTeam.name !== team.name || originalTeam.supervisorTeam !== team.supervisorTeam;
      return {
        name: team.name,
        state: somethingChanged ? TeamImportState.Edited : TeamImportState.Unchanged,
        supervisorTeam: team.supervisorTeam || undefined,
        id: originalTeam.id,
      };
    }
    return {
      name: team.name,
      state: TeamImportState.New,
      supervisorTeam: team.supervisorTeam || undefined,
      id: undefined,
    };
  });

  return teamsWithAdditionalData;
};
